import ENV from "@/utils/env";
import { createAuthState, normalizeReturnTo } from "@/features/auth/utils/auth-state";
import { AuthLoader } from "@/features/auth/components/auth-loader";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useEffect } from "react";
import { useLocation } from "wouter";

/**
 * SignInRoute component
 *
 * Routes users through the Auth0 rollout when enabled and falls back to the legacy iframe auth flow otherwise.
 */
export const SignInRoute = () => {
  const isAuth0RolloutEnabled = useFeatureFlag("auth0Rollout");

  return isAuth0RolloutEnabled ? <Auth0SignInRoute /> : <LegacySignInRoute />;
};

/**
 * buildAuth0AuthorizeUrl function
 *
 * Builds the Auth0 authorization URL for the redirect-based sign-in flow.
 *
 * @param returnTo - Optional return target to restore after authentication.
 * @returns The full Auth0 authorize URL.
 */
export const buildAuth0AuthorizeUrl = (returnTo: string | null) => {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: ENV.AUTH0_CLIENT_ID,
    redirect_uri: ENV.AUTH0_CALLBACK_URL,
    scope: "openid profile email",
    state: createAuthState(returnTo),
  });

  return `${ENV.AUTH0_DOMAIN}/authorize?${params.toString()}`;
};

/**
 * Auth0SignInRoute component
 *
 * Performs a full-page redirect to the Auth0 authorization endpoint.
 */
const Auth0SignInRoute = () => {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnTo = normalizeReturnTo(urlParams.get("returnTo"));

    // Redirect to Auth0 for login
    window.location.replace(buildAuth0AuthorizeUrl(returnTo));
  }, []);

  return <AuthLoader />;
};

/**
 * LegacySignInRoute component
 *
 * Loads the embedded auth iframe and listens for successful auth messages.
 */
const LegacySignInRoute = () => {
  const [, navigate] = useLocation();

  useEffect(() => {
    const handler = (event: MessageEvent<{ type?: string; session?: { productAccess?: string[] } }>) => {
      if (event.data?.type === "auth" && event.data.session?.productAccess?.includes("studio")) {
        navigate("/", { replace: true });
      }
    };

    window.addEventListener("message", handler);

    return () => {
      window.removeEventListener("message", handler);
    };
  }, [navigate]);

  return <iframe src={ENV.VITE_AUTH_URL} title="Sign in" className="flex flex-col items-center justify-center h-screen w-screen" />;
};
